import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useParams, useSearchParams} from 'react-router-dom';
import useAppContext from '../../contexts/UseAppContext';
import DataLoader from '../../components/DataLoader/DataLoader';
import DataError from '../../components/DataError/DataError';
import ProductTitle from '../../components/ProductTitle/ProductTitle';
import ProductGallery from '../../components/ProductGallery/ProductGallery';
import NoStockMessage from '../../components/NoStockMessage/NoStockMessage';
import VariantPicker from '../../components/VariantPicker/VariantPicker';
import TabManager from '../../components/TabManager/TabManager';
import PricesBox from '../../components/PricesBox/PricesBox';
import Button from '../../components/Button/Button';
import ProductReplaceModal from './ProductReplaceModal/ProductReplaceModal';
import MaxItemsModal from './MaxItemsModal/MaxItemsModal';
import Activity from '../../helpers/Activity';
import Navigation from '../../config/Navigation';
import ExternalAnalytics from '../../helpers/ExternalAnalytics';
import Debug from '../../helpers/Debug';
import {Product as _Product} from '../../models/Product';
import {firestore, getCollectionPath} from '../../config/Firebase';
import {collection, doc, onSnapshot, query} from 'firebase/firestore';
import {productConverter, featuresMapConverter} from '../../models/Product';
import './Product.css';

function Product({section}) {
    const context = useAppContext();
    const navigate = useNavigate();
    const urlParams = useParams();
    const [searchParams] = useSearchParams();
    const productId = urlParams.productId;
    const variantId = searchParams.get('variant');
    const storeId = searchParams.get('oda');
    const scannerLinkLabel = 'Escanear otro producto';
    const cartLinkLabel = 'Lo quiero';
    const replaceModalAcceptLabel = 'Sí, deseo reemplazarlo';
    const replaceModalRejectLabel = 'No, mantener el anterior';
    const [isLoadingProduct, setIsLoadingProduct] = useState(true);
    const [productLoaded, setProductLoaded] = useState(false);
    const [productError, setProductError] = useState(undefined);
    const [product, setProduct] = useState(undefined);
    const [variants, setVariants] = useState([]);
    const [variant, setVariant] = useState(undefined);
    const [isLoadingFeaturesMap, setIsLoadingFeaturesMap] = useState(true);
    const [featuresMapLoaded, setFeaturesMapLoaded] = useState(false);
    const [featuresMap, setFeaturesMap] = useState([]);
    const [tab, setTab] = useState(undefined);
    const [showReplaceModal, setShowReplaceModal] = useState(false);
    const [showMaxItemsModal, setShowMaxItemsModal] = useState(false);
    let unsubscribeProduct = undefined;
    let unsubscribeFeaturesMap = undefined;

    useEffect(() => {
        getProduct();
        getFeaturesMap();
        return () => {
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getProduct = () => {
        if (storeId) context.setStore(storeId);
        const _path = getCollectionPath('items');
        const _doc = doc(firestore, _path, productId).withConverter(productConverter);
        unsubscribeProduct = onSnapshot(_doc, (_document) => {
            try {
                const exists = _document.exists();
                if (!exists) throw new Error('S&G-no-product');
                const _product = _document.data();
                const _variants = _product.variants; // Show all variants even if they don't have stock or prices.
                let _variant = undefined;
                if (!variant && _variants && _variants.length > 0) {
                    if (variantId) {
                        const match = _variants.find((_variant) => _variant.id === variantId);
                        if (match) _variant = variantId;
                        else _variant = _variants[0].id;
                    } else _variant = _variants[0].id;
                }
                onLoadSuccess(_product, _variants, _variant);
            } catch (error) {
                const message = error.message;
                Debug.printToLog('error', error);
                onLoadError(message.includes('S&G') ? message : 'S&G-init-error');
            }
        }, (error) => {
            Debug.printToLog('error', error);
            onLoadError('S&G-load-error');
        });
    };

    const getFeaturesMap = () => {
        setIsLoadingFeaturesMap(true);
        setFeaturesMapLoaded(false);
        const _path = getCollectionPath('i18n/es/feature_keys');
        const _collection = collection(firestore, _path);
        const _query = query(_collection).withConverter(featuresMapConverter);
        unsubscribeFeaturesMap = onSnapshot(_query, (_snapshot) => {
            try {
                setFeaturesMap((prevFeaturesMap) => {
                    let _featuresMap = [...prevFeaturesMap];
                    _snapshot.docChanges().forEach((change) => {
                        if (change.type === 'added') {
                            _featuresMap.push(change.doc.data());
                        } else if (change.type === 'modified') {
                            const index = _featuresMap.findIndex(f => f.id === change.doc.id);
                            if (index >= 0) _featuresMap[index] = change.doc.data();
                        } else if (change.type === 'removed') {
                            const index = _featuresMap.findIndex(f => f.id === change.doc.id);
                            if (index >= 0) _featuresMap.splice(index, 1);
                        }
                    });
                    return _featuresMap;
                });
                setIsLoadingFeaturesMap(false);
                setFeaturesMapLoaded(true);
            } catch (error) {
                Debug.printToLog('error', error);
                setIsLoadingFeaturesMap(false);
                setFeaturesMapLoaded(false);
            }
        }, (error) => {
            Debug.printToLog('error', error);
            setIsLoadingFeaturesMap(false);
            setFeaturesMapLoaded(false);
        });
    };

    const unsubscribe = () => {
        if (unsubscribeProduct) unsubscribeProduct();
        if (unsubscribeFeaturesMap) unsubscribeFeaturesMap();
    };

    const onLoadSuccess = (_product, _variants, _variant) => {
        setProduct(_product);
        setVariants(_variants);
        setVariant(_variant);
        setTab(_Product.getMainInformationSource(_product, _variant));
        setProductError(undefined);
        setProductLoaded(true);
        setIsLoadingProduct(false);
    };

    const onLoadError = (type) => {
        setProduct(undefined);
        setVariants([]);
        setVariant(undefined);
        setTab(undefined);
        setProductError(type);
        setProductLoaded(false);
        setIsLoadingProduct(false);
    };

    useEffect(() => {
        if (product && variant) ExternalAnalytics.viewItem(getVariant() || product, section);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product, variant]);

    const toHome = (error, sendExternalMetric) => {
        if (sendExternalMetric) ExternalAnalytics.goTo(getVariant() || product, section, 'link', scannerLinkLabel);
        Activity.log('product-view', 'scan-button', 'click', getMetadata({error: error || null}));
        const url = Navigation.getHomeUrl();
        navigate(url);
    };

    const toCart = (addedProduct) => {
        if (addedProduct) ExternalAnalytics.addToCart(getVariant() || product, section);
        const url = Navigation.getCartUrl();
        navigate(url);
    };

    const reload = (error) => {
        Activity.log('product-view', 'reload-button', 'click', getMetadata({error: error || null}))
            .then(() => {
                Navigation.reload();
            });
    };

    const getEntity = () => {
        return getVariant() || product;
    };

    const getVariant = () => {
        return variants && variants.length > 0 ? variants.find((_variant) => _variant.id === variant) : undefined;
    };

    const getMetadata = (additionalData) => {
        const variant = getVariant();
        const metadata = {
            product_id: product ? product.id : null,
            product_sku: product ? product.sku : null,
            product_name: product ? product.name : null,
            variant_id: variant ? variant.id : null,
            variant_sku: variant ? variant.sku : null,
            variant_name: variant ? variant.name : null
        };
        if (additionalData) return {...metadata, ...additionalData};
        else return metadata;
    };

    const onGallerySlide = (index, image) => {
        Activity.log('product-view', 'image-gallery', 'change-image', getMetadata({index: index, image: image}));
    };

    const onVariantChange = (_variant) => {
        setTab(_Product.getMainInformationSource(product, _variant));
        setVariant(_variant);
    };

    useEffect(() => {
        const _variant = getVariant();
        if (_variant) Activity.log('product-view', 'variants-list', 'set-variant', getMetadata());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variant]);

    const hasTabs = () => {
        const tabs = getTabs();
        return tabs && tabs.length > 0;
    };

    const getTabs = () => {
        const entity = getEntity();
        let tabs = [];
        if (entity.longDescription) tabs.push({id: 'description', type: 'html', title: 'Descripción', content: entity.longDescription, map: undefined, isLoading: false, loaded: true});
        if (entity.features && entity.features.length > 0) tabs.push({id: 'technical-data', type: 'table', title: 'Ficha técnica', content: entity.features, map: featuresMap, isLoading: isLoadingFeaturesMap, loaded: featuresMapLoaded});
        return tabs;
    };

    const onTabChange = (_tab) => {
        const match = getTabs().find((tab) => tab.id === _tab);
        if (match) {
            Activity.log('product-view', 'tabs-list', 'set-tab', getMetadata({tab: tab}));
            ExternalAnalytics.productDetailsInteraction(getVariant() || product, section, 'tab', match.title);
            setTab(_tab);
        }
    };

    const addToCart = () => {
        const entity = getEntity();
        const cartMode = context.cartMode;
        const shoppingCart = context.shoppingCart;
        if (shoppingCart && shoppingCart.store !== context.store) { // If the store is different, replace the cart.
            const item = entity.toCartItem();
            context.replaceAllItemsInCart(item, context.store);
            toCart(true);
        } else {
            if (cartMode === 'full') {
                const match = shoppingCart ? shoppingCart.getItemByProduct(entity.id) : undefined;
                if (match) {
                    Activity.log('product-view', 'pay-button', 'add-to-cart', getMetadata({status: 'already-in-cart'}));
                    toCart(false);
                } else {
                    if (shoppingCart && shoppingCart.hasItems() && shoppingCart.items.length >= context.fullCartModeMaxItems) {
                        Activity.log('product-view', 'max-items-modal', 'open', getMetadata());
                        openMaxItemsModal();
                    } else {
                        const item = entity.toCartItem();
                        context.addItemToCart(item);
                        Activity.log('product-view', 'pay-button', 'add-to-cart', getMetadata({status: 'item-added'}));
                        toCart(true);
                    }
                }
            } else if (cartMode === 'single') {
                if (shoppingCart && shoppingCart.items && shoppingCart.items.length > 0) {
                    const match = shoppingCart ? shoppingCart.getItemByProduct(entity.id) : undefined;
                    if (match) {
                        Activity.log('product-view', 'pay-button', 'add-to-cart', getMetadata({status: 'already-in-cart'}));
                        toCart(false);
                    } else {
                        Activity.log('product-view', 'replace-modal', 'open', getMetadata());
                        ExternalAnalytics.load(getVariant() || product, section, 'modal', 'carga');
                        openReplaceModal();
                    }
                } else {
                    const item = entity.toCartItem();
                    context.addItemToCart(item);
                    Activity.log('product-view', 'pay-button', 'add-to-cart', getMetadata({status: 'item-added'}));
                    toCart(true);
                }
            }
        }
    };

    const openReplaceModal = () => {
        setShowReplaceModal(true);
    };

    const closeReplaceModal = () => {
        setShowReplaceModal(false);
    };

    const onReplaceSelect = (replace) => {
        Activity.log('product-view', 'replace-modal', replace ? 'replace' : 'cancel', getMetadata());
        ExternalAnalytics.clickToAction(getVariant() || product, section, 'modal-button', replace ? replaceModalAcceptLabel : replaceModalRejectLabel);
        if (replace) {
            const entity = getEntity();
            const newItem = entity.toCartItem();
            context.replaceAllItemsInCart(newItem, undefined);
        }
        toCart(replace);
    };

    const openMaxItemsModal = () => {
        setShowMaxItemsModal(true);
    };

    const closeMaxItemsModal = () => {
        Activity.log('product-view', 'max-items-modal', 'close', getMetadata());
        setShowMaxItemsModal(false);
    };

    const onCartSelect = () => {
        Activity.log('product-view', 'max-items-modal', 'to-cart', getMetadata());
        toCart(false);
    };

    const wrapperStyle = () => {
        return {
            marginBottom: context.productScanButtonHeight + context.productAddToCartButtonHeight + 20 // 20 is an additional margin.
        };
    };

    const renderLoader = () => {
        return (
            <div className='product_data-loader'>
                <DataLoader message='Obteniendo información del producto...' loaderSize={undefined}/>
            </div>
        );
    };

    const renderError = () => {
        let message;
        let actionLabel;
        let action;
        switch (productError) {
            case 'S&G-no-product':
                message = 'No hemos encontrado el producto que buscas';
                actionLabel = scannerLinkLabel;
                action = () => toHome(productError, false);
                break;
            case 'S&G-load-error':
                message = 'Se ha producido un error al obtener la información del producto';
                actionLabel = 'Reintentar';
                action = () => reload(productError, false);
                break;
            case 'S&G-init-error':
                message = 'Se ha producido un error al cargar el producto';
                actionLabel = scannerLinkLabel;
                action = () => toHome(productError, false);
                break;
            default:
                break;
        }
        if (message) {
            return (
                <div className='product_data-error'>
                    <DataError message={message} actionLabel={actionLabel} action={action} padding={20} maxWidth={undefined} iconSize={undefined} fontSize={undefined}/>
                </div>
            );
        } else return undefined;
    };

    const renderContent = () => {
        const entity = getEntity();
        return (
            <div className='product_wrapper' style={wrapperStyle()}>
                <div className='product_product-title'>
                    <ProductTitle product={entity}/>
                </div>
                {entity.hasGallery() && (
                    <div className='product_product-gallery'>
                        <ProductGallery product={entity} disabled={!entity.hasStock()} onSlide={onGallerySlide}/>
                    </div>
                )}
                {!entity.hasStock() && (
                    <div className='product_no-stock-message'>
                        <NoStockMessage/>
                    </div>
                )}
                {variants && variants.length > 0 && (
                    <div className='product_variant-picker'>
                        <VariantPicker product={product} variants={variants} variant={variant} title='Color' onVariantClick={onVariantChange}/>
                    </div>
                )}
                <div className='product_prices-box'>
                    <PricesBox product={entity} mode='product'/>
                </div>
                {hasTabs() && (
                    <div className='product_tab-manager'>
                        <TabManager tabs={getTabs()} selectedTab={tab} onTabClick={onTabChange}/>
                    </div>
                )}
                <div className='product_footer'>
                    <div className='product_buttons'>
                        <div className='product_button'>
                            <Button
                                icon={undefined}
                                label={scannerLinkLabel}
                                labelColor='#FFFFFF'
                                backgroundColor='#FF3D00'
                                borderColor='#FF3D00'
                                borderWidth={0}
                                borderRadius={0}
                                padding='15px 10px'
                                paddingTarget='label'
                                margin={0}
                                width='100%'
                                minWidth={undefined}
                                maxWidth={undefined}
                                height={context.productScanButtonHeight}
                                minHeight={undefined}
                                maxHeight={undefined}
                                transition='label'
                                fontSize={18}
                                boxShadow='unset'
                                isLoading={false}
                                loadLabel={undefined}
                                disabled={false}
                                showPulseEffect={false}
                                notificationNumber={undefined}
                                notificationBackgroundColor={undefined}
                                notificationLabelColor={undefined}
                                onClick={() => toHome(undefined, true)}
                            />
                        </div>
                        {entity.isValid() && (
                            <div className='product_button'>
                                <Button
                                    icon={undefined}
                                    label={cartLinkLabel}
                                    labelColor='#FFFFFF'
                                    backgroundColor='#002EFF'
                                    borderColor='#002EFF'
                                    borderWidth={0}
                                    borderRadius={0}
                                    padding='15px 10px'
                                    paddingTarget='label'
                                    margin={0}
                                    width='100%'
                                    minWidth={undefined}
                                    maxWidth={undefined}
                                    height={context.productAddToCartButtonHeight}
                                    minHeight={undefined}
                                    maxHeight={undefined}
                                    transition='label'
                                    fontSize={18}
                                    boxShadow='unset'
                                    isLoading={false}
                                    loadLabel={undefined}
                                    disabled={false}
                                    showPulseEffect={false}
                                    notificationNumber={undefined}
                                    notificationBackgroundColor={undefined}
                                    notificationLabelColor={undefined}
                                    onClick={addToCart}
                                />
                            </div>
                        )}
                    </div>
                </div>
                {showReplaceModal && (
                    <div className='product_product-replace-modal'>
                        <ProductReplaceModal acceptLabel={replaceModalAcceptLabel} rejectLabel={replaceModalRejectLabel} onSelect={onReplaceSelect} onClose={closeReplaceModal}/>
                    </div>
                )}
                {showMaxItemsModal && (
                    <div className='product_product-replace-modal'>
                        <MaxItemsModal limit={context.fullCartModeMaxItems} toCart={onCartSelect} onClose={closeMaxItemsModal}/>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className='product'>
            {isLoadingProduct && renderLoader()}
            {!isLoadingProduct && !productLoaded && renderError()}
            {!isLoadingProduct && productLoaded && renderContent()}
        </div>
    );
}

export default Product;